import "@xyflow/react/dist/style.css";
import "./U2000ReactFlowDialog.css";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import type { IconType } from "react-icons";
import { FaChevronRight, FaPlus, FaSave, FaUndo } from "react-icons/fa";
import dagre from "dagre";
import U2000BasicDialog from "./U2000BasicDialog";
import {
  applyEdgeChanges,
  applyNodeChanges,
  ReactFlow,
  ReactFlowProvider,
  useNodesInitialized,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type FitViewOptions,
  type Node,
  type NodeChange,
  type NodeTypes,
  type OnConnect,
} from "@xyflow/react";
import U2000ComplexNode from "../visual/U2000ComplexNode";
import U2000UnsavedNodePositionsDialog, {
  type U2000UnsavedNodePositionsDialogLabels,
} from "./U2000UnsavedNodePositionsDialog";
import {
  u2000CollectNodePositionsFromNodes,
  u2000ExtendPositionsBaselineForNewNodes,
  u2000IsPositionsMapDirty,
  type U2000ReactFlowNodePositionsMap,
} from "../../tools/u2000ReactFlowNodePositions";

const U2000_EDGE_STROKE_DEFAULT = "#171717";
/** Rouge vif — contraste net sur fond gris et lignes noires */
const U2000_EDGE_STROKE_SELECTED = "#dc2626";
const U2000_EDGE_WIDTH_DEFAULT = 2;
const U2000_EDGE_WIDTH_SELECTED = 5;

function u2000ApplyEdgeSelectionVisual(edge: Edge): Edge {
  const selected = edge.selected === true;
  const stroke = selected ? U2000_EDGE_STROKE_SELECTED : U2000_EDGE_STROKE_DEFAULT;
  const strokeWidth = selected ? U2000_EDGE_WIDTH_SELECTED : U2000_EDGE_WIDTH_DEFAULT;
  const colorMarker = (marker: Edge["markerStart"]) =>
    marker && typeof marker === "object" ? { ...marker, color: stroke } : marker;
  const next: Edge = {
    ...edge,
    style: { ...edge.style, stroke, strokeWidth },
    markerStart: colorMarker(edge.markerStart),
    zIndex: selected ? 1000 : 0,
  };
  if (edge.markerEnd != null) {
    next.markerEnd = colorMarker(edge.markerEnd);
  }
  return next;
}

function u2000MapEdgesSelectionVisual(edges: Edge[]): Edge[] {
  return edges.map(u2000ApplyEdgeSelectionVisual);
}

// Ajuste ici la taille pour le layout dagre (doit correspondre à la taille visuelle de tes nodes)
const nodeWidth = 40;
const nodeHeight = 20;
const nodePadding = 200; // Espace supplémentaire entre les nodes

function getLayoutedElements(nodes: Node[], edges: Edge[], direction: "TB" | "LR" = "TB"): { nodes: Node[]; edges: Edge[] } {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 120,
    ranksep: 160,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth + nodePadding, height: nodeHeight + nodePadding });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  return {
    nodes: nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      return {
        ...node,
        position: {
          x: nodeWithPosition.x - (nodeWidth + nodePadding) / 2,
          y: nodeWithPosition.y - (nodeHeight + nodePadding) / 2,
        },
      };
    }),
    edges,
  };
}

/** Position persistée côté API (ex. U3141 `nodePosition` sur le payload nœud). */
function nodeHasSavedPosition(node: Node): boolean {
  const payload = node.data?.nodePayload as { nodePosition?: { x: number; y: number } | null } | undefined;
  const np = payload?.nodePosition;
  return np != null && typeof np.x === "number" && typeof np.y === "number";
}

/**
 * Layout initial : dagre si aucune position sauvegardée ; positions API conservées ;
 * si mélange (ex. ajout lot + arbre concordance), dagre pour les nœuds sans `nodePosition` seulement.
 */
function resolveInitialGraphLayout(nodes: Node[], edges: Edge[]): { nodes: Node[]; edges: Edge[] } {
  if (nodes.length === 0) return { nodes, edges };
  const savedCount = nodes.filter(nodeHasSavedPosition).length;
  if (savedCount === 0) {
    return getLayoutedElements(nodes, edges);
  }
  if (savedCount === nodes.length) {
    return { nodes, edges };
  }
  const layouted = getLayoutedElements(nodes, edges);
  const positionById = new Map(layouted.nodes.map((n) => [n.id, n.position]));
  return {
    nodes: nodes.map((n) =>
      nodeHasSavedPosition(n) ? n : { ...n, position: positionById.get(n.id) ?? n.position },
    ),
    edges: layouted.edges,
  };
}

/** Rafraîchissement lotChainGraph (link/unlink/create) : libellés à jour, positions canvas inchangées. */
function u2000MergeGraphSnapshotPreservingLayout(currentNodes: Node[], incomingNodes: Node[]): Node[] {
  const currentById = new Map(currentNodes.map((n) => [n.id, n]));
  return incomingNodes.map((inc) => {
    const cur = currentById.get(inc.id);
    if (!cur) {
      return inc;
    }
    return {
      ...inc,
      position: cur.position,
      selected: cur.selected,
      dragging: cur.dragging,
    };
  });
}

const nodeTypes = {
  u2000Complex: U2000ComplexNode,
} as NodeTypes;

/** Cadrage : marge, zoom assez bas pour une chaîne large, plafond pour une chaîne courte. */
const flowFitView: FitViewOptions = { padding: 0.12, minZoom: 0.05, maxZoom: 1.25 };

export interface U2000ReactFlowPaneContextOpenDetail {
  clientX: number;
  clientY: number;
  flowPosition: { x: number; y: number };
}

interface U2000ReactFlowCanvasProps {
  nodes: Node[];
  edges: Edge[];
  onNodesChange: (changes: NodeChange[]) => void;
  onEdgesChange?: (changes: EdgeChange[]) => void;
  onNodeContextMenu?: (event: React.MouseEvent, node: Node) => void;
  onEdgeContextMenu?: (event: React.MouseEvent, edge: Edge) => void;
  onPaneClick: () => void;
  onPaneContextMenu: (event: React.MouseEvent | MouseEvent) => void;
  onPaneContextMenuOpen?: (detail: U2000ReactFlowPaneContextOpenDetail) => void;
  onConnect?: OnConnect;
  isValidConnection?: (connection: Edge | Connection) => boolean;
  onEdgesDelete?: (edges: Edge[]) => void;
  onEdgeClick?: (event: React.MouseEvent, edge: Edge) => void;
  nodesConnectable?: boolean;
  edgesDeletable?: boolean;
}

/** Doit rester sous ReactFlowProvider : le cadrage attend que xyflow ait mesuré les cartes. */
const U2000ReactFlowCanvas: React.FC<U2000ReactFlowCanvasProps> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onNodeContextMenu,
  onEdgeContextMenu,
  onPaneClick,
  onPaneContextMenu,
  onPaneContextMenuOpen,
  onConnect,
  isValidConnection,
  onEdgesDelete,
  onEdgeClick,
  nodesConnectable = false,
  edgesDeletable = false,
}) => {
  const { fitView, screenToFlowPosition } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const nodeIdsKey = useMemo(() => nodes.map((node) => node.id).join("\0"), [nodes]);

  useEffect(() => {
    if (!nodesInitialized || nodes.length === 0) return;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      void fitView({ ...flowFitView, duration: 0 });
    };
    const frame = requestAnimationFrame(run);
    const timer = window.setTimeout(run, 280);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [nodesInitialized, nodeIdsKey, nodes.length, fitView]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      isValidConnection={isValidConnection}
      onEdgesDelete={onEdgesDelete}
      onEdgeClick={onEdgeClick}
      nodesConnectable={nodesConnectable}
      edgesFocusable
      edgesSelectable
      edgesDeletable={edgesDeletable}
      deleteKeyCode={edgesDeletable ? ["Backspace", "Delete"] : null}
      defaultEdgeOptions={{ interactionWidth: 24 }}
      connectionLineStyle={{ stroke: U2000_EDGE_STROKE_DEFAULT, strokeWidth: 2 }}
      fitView
      fitViewOptions={flowFitView}
      minZoom={0.05}
      maxZoom={2}
      nodeTypes={nodeTypes}
      nodesDraggable={true}
      nodesSelectable
      elementsSelectable
      className="bg-gray-50"
      attributionPosition="bottom-left"
      onNodeContextMenu={onNodeContextMenu}
      onEdgeContextMenu={onEdgeContextMenu}
      onPaneClick={onPaneClick}
      onPaneContextMenu={(event) => {
        onPaneContextMenu(event);
        if (onPaneContextMenuOpen) {
          const clientX = 'clientX' in event ? event.clientX : 0;
          const clientY = 'clientY' in event ? event.clientY : 0;
          const flowPosition = screenToFlowPosition({ x: clientX, y: clientY });
          onPaneContextMenuOpen({ clientX, clientY, flowPosition });
        }
      }}
    />
  );
};

export type U2000ReactFlowContextMenuItemVariant = "action" | "document" | "dialog" | "destructive";

export interface U2000ReactFlowContextMenuItem {
  key: string;
  /** Si true, affiche une ligne horizontale (pas de libellé ni d’action). */
  separator?: boolean;
  /** Titre de section non interactif (libellé court — groupes du menu). */
  sectionHeader?: boolean;
  label?: ReactNode;
  /** Icône optionnelle (alignée toolbar U2000). */
  icon?: IconType;
  /** Style sémantique de la ligne (action, lien document, dialogue, destructif). */
  itemVariant?: U2000ReactFlowContextMenuItemVariant;
  /** Infobulle / aria-label si libellé tronqué. */
  title?: string;
  /** Optionnel pour les items désactivés (ex. « Pas de document »). */
  onClick?: (node: Node) => void;
  /** Désactive l’item (ex. document non disponible). Peut être une fonction du nœud pour un menu contextuel. */
  disabled?: boolean | ((node: Node) => boolean);
  /** Sous-menu flyout (ex. types de document importables). */
  subItems?: U2000ReactFlowContextMenuItem[];
}

/** Menu contextuel sur une arête (clic droit). */
export interface U2000ReactFlowEdgeContextMenuItem {
  key: string;
  separator?: boolean;
  sectionHeader?: boolean;
  label?: ReactNode;
  icon?: IconType;
  itemVariant?: U2000ReactFlowContextMenuItemVariant;
  title?: string;
  onClick?: (edge: Edge) => void;
  disabled?: boolean | ((edge: Edge) => boolean);
}

/** Item du menu contextuel sur le pane (clic droit sur le fond du graphe). */
export interface U2000ReactFlowPaneContextMenuItem {
  key: string;
  label: string;
  onClick: () => void;
  icon?: IconType;
  itemVariant?: U2000ReactFlowContextMenuItemVariant;
  disabled?: boolean;
}

type U2000ReactFlowContextMenuSection = {
  headerKey: string;
  headerLabel: ReactNode | null;
  items: U2000ReactFlowContextMenuItem[];
};

function u2000GroupReactFlowContextMenuItems(items: U2000ReactFlowContextMenuItem[]): U2000ReactFlowContextMenuSection[] {
  const sections: U2000ReactFlowContextMenuSection[] = [];
  const loose: U2000ReactFlowContextMenuItem[] = [];
  let current: U2000ReactFlowContextMenuSection | null = null;

  for (const item of items) {
    if (item.separator) {
      continue;
    }
    if (item.sectionHeader) {
      if (current) {
        sections.push(current);
      }
      current = { headerKey: item.key, headerLabel: item.label ?? "", items: [] };
      continue;
    }
    if (current) {
      current.items.push(item);
    } else {
      loose.push(item);
    }
  }
  if (current) {
    sections.push(current);
  }
  if (loose.length > 0) {
    sections.unshift({ headerKey: "section-loose-items", headerLabel: null, items: loose });
  }
  return sections.filter((section) => section.items.length > 0);
}

const U2000_PANE_MENU_ADD_KEYS = new Set(["addLot", "add"]);

function u2000AppendReactFlowContextMenuSection(
  target: U2000ReactFlowContextMenuItem[],
  headerKey: string,
  headerLabel: string,
  sectionItems: U2000ReactFlowContextMenuItem[],
): void {
  if (sectionItems.length === 0) {
    return;
  }
  target.push({ key: headerKey, label: headerLabel, sectionHeader: true });
  target.push(...sectionItems);
}

function u2000EstimateContextMenuItemRowHeight(item: U2000ReactFlowContextMenuItem): number {
  if (item.sectionHeader || item.separator) {
    return 0;
  }
  if (item.itemVariant === "document") {
    return 52;
  }
  if (typeof item.label !== "string") {
    return 48;
  }
  return 40;
}

function u2000EstimateGroupedContextMenuHeight(items: U2000ReactFlowContextMenuItem[]): number {
  const sections = u2000GroupReactFlowContextMenuItems(items);
  if (sections.length === 0) {
    return 16;
  }
  const sectionHeaderHeight = 22;
  const sectionGap = 8;
  const outerPadding = 16;
  let height = outerPadding;
  sections.forEach((section, index) => {
    if (section.headerLabel) {
      height += sectionHeaderHeight;
    }
    height +=
      section.items.reduce((sum, item) => sum + u2000EstimateContextMenuItemRowHeight(item), 0) + 2;
    if (index < sections.length - 1) {
      height += sectionGap;
    }
  });
  return height;
}

const U2000_CONTEXT_MENU_VIEWPORT_MARGIN = 8;

/** Ancrage vertical : ouverture vers le bas par défaut ; si place insuffisante, ouverture vers le haut (aligné sur le curseur). */
function u2000ResolveContextMenuVerticalTop(clientY: number, menuHeight: number): number {
  const margin = U2000_CONTEXT_MENU_VIEWPORT_MARGIN;
  const viewportTop = margin;
  const viewportBottom = window.innerHeight - margin;
  const maxMenuHeight = viewportBottom - viewportTop;
  const layoutHeight = Math.min(Math.max(menuHeight, 0), maxMenuHeight);

  const spaceBelow = viewportBottom - clientY;
  const spaceAbove = clientY - viewportTop;

  if (layoutHeight <= spaceBelow) {
    return clientY;
  }

  if (layoutHeight <= spaceAbove) {
    return clientY - layoutHeight;
  }

  if (spaceAbove >= spaceBelow) {
    return Math.max(viewportTop, clientY - layoutHeight);
  }

  return Math.max(viewportTop, viewportBottom - layoutHeight);
}

function u2000ReactFlowContextMenuItemRowClass(variant: U2000ReactFlowContextMenuItemVariant | undefined): string {
  switch (variant) {
    case "document":
      return "text-gray-800";
    case "dialog":
      return "font-medium text-gray-900";
    case "destructive":
      return "text-red-700";
    default:
      return "text-gray-800";
  }
}

type U2000ReactFlowGroupedContextMenuProps = {
  items: U2000ReactFlowContextMenuItem[];
  menuAriaLabel: string;
  position: { left: number; top: number };
  menuRef: React.RefObject<HTMLDivElement | null>;
  resolveDisabled: (item: U2000ReactFlowContextMenuItem) => boolean;
  onItemActivate: (item: U2000ReactFlowContextMenuItem) => void;
  minWidthClass?: string;
};

function u2000MeasureContextMenuTop(clientY: number, menuEl: HTMLElement): number {
  return u2000ResolveContextMenuVerticalTop(clientY, menuEl.getBoundingClientRect().height);
}

const U2000_CONTEXT_MENU_FLYOUT_MIN_WIDTH = 200;
const U2000_CONTEXT_MENU_FLYOUT_MARGIN = 8;

function u2000ResolveContextMenuFlyoutPosition(anchorRect: DOMRect, flyoutHeight: number): { left: number; top: number } {
  const margin = U2000_CONTEXT_MENU_FLYOUT_MARGIN;
  const viewportRight = window.innerWidth - margin;
  const viewportLeft = margin;
  const viewportBottom = window.innerHeight - margin;
  const flyoutWidth = U2000_CONTEXT_MENU_FLYOUT_MIN_WIDTH;

  let left = anchorRect.right + 4;
  if (left + flyoutWidth > viewportRight) {
    left = Math.max(viewportLeft, anchorRect.left - flyoutWidth - 4);
  }

  let top = anchorRect.top;
  const maxTop = viewportBottom - flyoutHeight;
  if (top > maxTop) {
    top = Math.max(margin, maxTop);
  }

  return { left, top };
}

type U2000ReactFlowContextMenuFlyoutProps = {
  subItems: U2000ReactFlowContextMenuItem[];
  anchorRect: DOMRect;
  resolveDisabled: (item: U2000ReactFlowContextMenuItem) => boolean;
  onItemActivate: (item: U2000ReactFlowContextMenuItem) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
};

function U2000ReactFlowContextMenuFlyout({
  subItems,
  anchorRect,
  resolveDisabled,
  onItemActivate,
  onMouseEnter,
  onMouseLeave,
}: U2000ReactFlowContextMenuFlyoutProps) {
  const flyoutRef = useRef<HTMLDivElement>(null);
  const estimatedHeight = subItems.length * 40 + 16;
  const position = u2000ResolveContextMenuFlyoutPosition(anchorRect, estimatedHeight);

  useLayoutEffect(() => {
    const el = flyoutRef.current;
    if (!el) {
      return;
    }
    const rect = el.getBoundingClientRect();
    const adjusted = u2000ResolveContextMenuFlyoutPosition(anchorRect, rect.height);
    el.style.left = `${adjusted.left}px`;
    el.style.top = `${adjusted.top}px`;
  }, [anchorRect, subItems.length]);

  const flyoutNode = (
    <div
      ref={flyoutRef}
      className="fixed z-[1201] min-w-[200px] max-h-[calc(100dvh-16px)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-1 shadow-xl"
      style={{ left: position.left, top: position.top }}
      role="menu"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {subItems.map((subItem, subIndex) => {
        const subDisabled = resolveDisabled(subItem);
        const SubIcon = subItem.icon;
        const subVariantClass = u2000ReactFlowContextMenuItemRowClass(subItem.itemVariant);
        return (
          <button
            key={subItem.key}
            type="button"
            role="menuitem"
            disabled={subDisabled}
            aria-disabled={subDisabled}
            title={subItem.title}
            className={`flex w-full min-w-0 items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${subVariantClass}${
              subIndex < subItems.length - 1 ? " border-b border-gray-100" : ""
            }`}
            onClick={() => {
              if (subDisabled) return;
              onItemActivate(subItem);
            }}
          >
            {SubIcon ? (
              <SubIcon
                className={`size-4 shrink-0 ${subItem.itemVariant === "destructive" ? "text-red-600" : "text-gray-500"}`}
                aria-hidden
              />
            ) : null}
            <span className={`min-w-0 flex-1 ${typeof subItem.label === "string" ? "truncate" : ""}`}>{subItem.label}</span>
          </button>
        );
      })}
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(flyoutNode, document.body);
}

function U2000ReactFlowGroupedContextMenu({
  items,
  menuAriaLabel,
  position,
  menuRef,
  resolveDisabled,
  onItemActivate,
  minWidthClass = "min-w-[200px]",
}: U2000ReactFlowGroupedContextMenuProps) {
  const sections = useMemo(() => u2000GroupReactFlowContextMenuItems(items), [items]);
  const [renderTop, setRenderTop] = useState(position.top);
  const [openFlyoutKey, setOpenFlyoutKey] = useState<string | null>(null);
  const [flyoutAnchorRect, setFlyoutAnchorRect] = useState<DOMRect | null>(null);
  const flyoutCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFlyoutCloseTimer = useCallback(() => {
    if (flyoutCloseTimerRef.current) {
      clearTimeout(flyoutCloseTimerRef.current);
      flyoutCloseTimerRef.current = null;
    }
  }, []);

  const scheduleFlyoutClose = useCallback(() => {
    clearFlyoutCloseTimer();
    flyoutCloseTimerRef.current = setTimeout(() => {
      setOpenFlyoutKey(null);
      setFlyoutAnchorRect(null);
    }, 200);
  }, [clearFlyoutCloseTimer]);

  const openFlyoutForItem = useCallback(
    (item: U2000ReactFlowContextMenuItem, anchorEl: HTMLElement) => {
      if (!item.subItems?.length) {
        return;
      }
      clearFlyoutCloseTimer();
      setOpenFlyoutKey(item.key);
      setFlyoutAnchorRect(anchorEl.getBoundingClientRect());
    },
    [clearFlyoutCloseTimer],
  );

  useLayoutEffect(() => {
    const menuEl = menuRef.current;
    if (!menuEl) {
      return;
    }
    setRenderTop(u2000MeasureContextMenuTop(position.top, menuEl));
  }, [position.left, position.top, items, menuRef]);

  useEffect(() => {
    setOpenFlyoutKey(null);
    setFlyoutAnchorRect(null);
  }, [items, position.left, position.top]);

  const openFlyoutItem = useMemo(() => {
    if (!openFlyoutKey) {
      return null;
    }
    for (const section of sections) {
      const found = section.items.find((item) => item.key === openFlyoutKey);
      if (found?.subItems?.length) {
        return found;
      }
    }
    return null;
  }, [openFlyoutKey, sections]);

  const menuNode = (
    <div
      ref={menuRef}
      className={`fixed z-[1200] ${minWidthClass} max-h-[calc(100dvh-16px)] max-w-[min(100vw-16px,420px)] overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-gray-100 p-2 shadow-xl`}
      style={{ left: position.left, top: renderTop }}
      role="menu"
      aria-label={menuAriaLabel}
    >
      {sections.map((section, sectionIndex) => (
        <div key={section.headerKey} className={sectionIndex < sections.length - 1 ? "mb-2" : undefined}>
          <div
            className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm"
            role="group"
            aria-label={typeof section.headerLabel === "string" ? section.headerLabel : undefined}
          >
            {section.headerLabel ? (
              <div
                role="presentation"
                className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600"
              >
                {section.headerLabel}
              </div>
            ) : null}
            {section.items.map((item, itemIndex) => {
              const disabled = resolveDisabled(item);
              const Icon = item.icon;
              const variantClass = u2000ReactFlowContextMenuItemRowClass(item.itemVariant);
              const isDocumentRow = item.itemVariant === "document";
              const hasSubMenu = Boolean(item.subItems?.length);
              return (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  disabled={disabled}
                  aria-disabled={disabled}
                  aria-haspopup={hasSubMenu ? "menu" : undefined}
                  aria-expanded={hasSubMenu && openFlyoutKey === item.key ? true : undefined}
                  title={item.title}
                  className={`flex w-full min-w-0 items-center gap-2.5 px-3 text-left text-sm transition-colors hover:bg-gray-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${variantClass}${
                    isDocumentRow ? " py-2" : " py-2.5"
                  }${itemIndex < section.items.length - 1 ? " border-b border-gray-100" : ""}`}
                  onMouseEnter={(e) => {
                    if (hasSubMenu && !disabled) {
                      openFlyoutForItem(item, e.currentTarget);
                    }
                  }}
                  onMouseLeave={() => {
                    if (hasSubMenu) {
                      scheduleFlyoutClose();
                    }
                  }}
                  onClick={(e) => {
                    if (disabled) return;
                    if (hasSubMenu) {
                      openFlyoutForItem(item, e.currentTarget);
                      return;
                    }
                    onItemActivate(item);
                  }}
                >
                  {Icon ? (
                    <Icon
                      className={`size-4 shrink-0 ${item.itemVariant === "destructive" ? "text-red-600" : "text-gray-500"}`}
                      aria-hidden
                    />
                  ) : null}
                  <span className={`min-w-0 flex-1 ${typeof item.label === "string" ? "truncate" : ""}`}>{item.label}</span>
                  {hasSubMenu ? <FaChevronRight className="size-3 shrink-0 text-gray-400" aria-hidden /> : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return (
    <>
      {createPortal(menuNode, document.body)}
      {openFlyoutItem?.subItems?.length && flyoutAnchorRect ? (
        <U2000ReactFlowContextMenuFlyout
          subItems={openFlyoutItem.subItems}
          anchorRect={flyoutAnchorRect}
          resolveDisabled={resolveDisabled}
          onItemActivate={onItemActivate}
          onMouseEnter={clearFlyoutCloseTimer}
          onMouseLeave={scheduleFlyoutClose}
        />
      ) : null}
    </>
  );
}

export interface U2000ReactFlowDialogContextValue {
  onNodeVisibleChange?: (nodeId: string, visible: boolean) => void;
  showCheckbox?: boolean;
  /** Quand true, la checkbox « visible » est désactivée (ex. utilisateur sans droit EDIT). */
  checkboxDisabled?: boolean;
  /** True si le nœud a un ancêtre dont la checkbox est décochée (visible true) — la checkbox de ce nœud doit être désactivée. */
  isCheckboxDisabledByAncestor?: (nodeId: string) => boolean;
  masterLotChainId?: string;
}

const U2000ReactFlowDialogContext = createContext<U2000ReactFlowDialogContextValue | null>(null);

export function useU2000ReactFlowDialogContext(): U2000ReactFlowDialogContextValue | null {
  return useContext(U2000ReactFlowDialogContext);
}

/** Map des positions par childLotChainId (ou node id). */
export type U2000ReactFlowNodePositions = Record<string, { x: number; y: number }>;

interface U2000ReactFlowDialogProps {
  open: boolean;
  onClose: () => void;
  initialNodes: Node[];
  initialEdges: Edge[];
  title?: string;
  contextMenuItems?: U2000ReactFlowContextMenuItem[];
  /** Menu contextuel dynamique par nœud (prioritaire sur contextMenuItems si fourni). */
  getContextMenuItems?: (node: Node) => U2000ReactFlowContextMenuItem[];
  /** Menu contextuel sur une arête (clic droit). */
  getEdgeContextMenuItems?: (edge: Edge) => U2000ReactFlowEdgeContextMenuItem[];
  /** Menu contextuel sur le pane (clic droit sur le fond). Si non fourni mais onSavePositions/onResetLayout fournis, un menu par défaut est utilisé. */
  paneContextMenuItems?: U2000ReactFlowPaneContextMenuItem[];
  showCheckbox?: boolean;
  /** Désactive la checkbox « visible » (ex. utilisateur sans droit EDIT). */
  checkboxDisabled?: boolean;
  masterLotChainId?: string;
  /** Appelé avec la liste des ids concernés (nœud + descendants) et le booléen visible. Un seul appel pour un changement (bulk). */
  onNodeVisibleChange?: (params: { masterLotChainId: string; childLotChainIds: string[]; visible: boolean }) => void | Promise<void>;
  /** Sauvegarde des positions (clé = childLotChainId). Retourner `false` si échec (BL-0705 fermeture). */
  onSavePositions?: (positions: U2000ReactFlowNodePositions) => void | Promise<void | boolean>;
  /** Réinitialisation du layout (efface les positions, le parent refetch et repasse des initialNodes sans position). */
  onResetLayout?: () => void | Promise<void>;
  /** Libellé pour l'item « Sauvegarder les coordonnées » (i18n). */
  savePositionsLabel?: string;
  /** Libellé pour l'item « Réinitialiser le layout » (i18n). */
  resetLayoutLabel?: string;
  /** Titre de section pane — ajout de lot (i18n). */
  paneMenuSectionAddLabel?: string;
  /** Titre de section pane — disposition / coordonnées (i18n). */
  paneMenuSectionLayoutLabel?: string;
  /** Position flow au clic droit sur le pane (placement nœud principal). */
  onPaneContextMenuOpen?: (detail: U2000ReactFlowPaneContextOpenDetail) => void;
  /** BL-0663 — connexion manuelle entre ancrages top/bottom. */
  onConnect?: OnConnect;
  isValidConnection?: (connection: Edge | Connection) => boolean;
  /** BL-0664 — suppression d’arêtes (Delete). */
  onEdgesDelete?: (edges: Edge[]) => void;
  nodesConnectable?: boolean;
  edgesDeletable?: boolean;
  /** BL-0697 — première ouverture : resolveInitialGraphLayout une fois ; ensuite merge préservant le canvas. */
  initialGraphLayoutKey?: number;
  /** Positions courantes des nœuds (ex. create graphe U3141 sans déplacer l’existant). */
  liveGraphNodesRef?: React.MutableRefObject<Node[]>;
  /** BL-0705 — libellés confirmation fermeture (requis avec onSavePositions pour activer la garde). */
  unsavedPositionsCloseLabels?: U2000UnsavedNodePositionsDialogLabels;
  /** BL-0705 — ne pas intercepter la fermeture (ex. modale U3141 sœur ouverte). */
  suspendCloseGuard?: boolean;
  /** BL-0711 — incrémenter après persistance serveur des positions (ex. create lot graphe) pour resync baseline. */
  positionsBaselineCommitKey?: number;
}

const U2000ReactFlowDialog: React.FC<U2000ReactFlowDialogProps> = ({
  open,
  onClose,
  initialNodes,
  initialEdges,
  title,
  contextMenuItems = [],
  getContextMenuItems,
  getEdgeContextMenuItems,
  paneContextMenuItems: paneContextMenuItemsProp,
  showCheckbox = false,
  checkboxDisabled = false,
  masterLotChainId,
  onNodeVisibleChange,
  onSavePositions,
  onResetLayout,
  savePositionsLabel = "Sauvegarder les coordonnées",
  resetLayoutLabel = "Réinitialiser le layout",
  paneMenuSectionAddLabel = "Lot",
  paneMenuSectionLayoutLabel = "Disposition du graphe",
  onPaneContextMenuOpen,
  onConnect,
  isValidConnection,
  onEdgesDelete,
  nodesConnectable = false,
  edgesDeletable = false,
  initialGraphLayoutKey,
  liveGraphNodesRef,
  unsavedPositionsCloseLabels,
  suspendCloseGuard = false,
  positionsBaselineCommitKey,
}) => {
  const layouted = useMemo(() => {
    const resolved = resolveInitialGraphLayout(initialNodes, initialEdges);
    return {
      nodes: resolved.nodes,
      edges: u2000MapEdgesSelectionVisual(resolved.edges),
    };
  }, [initialNodes, initialEdges]);
  const [nodes, setNodes] = useState<Node[]>(layouted.nodes);
  const [edges, setEdges] = useState<Edge[]>(layouted.edges);
  const [contextMenu, setContextMenu] = useState<{ node: Node; x: number; y: number } | null>(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState<{ edge: Edge; x: number; y: number } | null>(null);
  const [paneContextMenu, setPaneContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [unsavedCloseConfirmOpen, setUnsavedCloseConfirmOpen] = useState(false);
  const [isSavingPositionsOnClose, setIsSavingPositionsOnClose] = useState(false);
  /** Dialog déjà ouvert : ne pas relancer dagre sur snapshot API (ex. Delete concordance). */
  const graphOpenSessionRef = useRef(false);
  const lastAppliedGraphLayoutKeyRef = useRef<number | undefined>(undefined);
  const positionsBaselineRef = useRef<U2000ReactFlowNodePositionsMap>({});
  const nodesRef = useRef<Node[]>(nodes);
  nodesRef.current = nodes;
  const baselineCommitKeySeenRef = useRef<number | undefined>(undefined);
  const pendingBaselineCommitKeyRef = useRef<number | undefined>(undefined);
  /** BL-0711 — après create lot U3141, étendre la baseline aux nouveaux ids avant le prochain test dirty. */
  const extendBaselineBeforeCloseCheckRef = useRef(false);

  const closeGuardEnabled = Boolean(onSavePositions && unsavedPositionsCloseLabels);

  const computeCurrentPositions = useCallback(
    (): U2000ReactFlowNodePositions => u2000CollectNodePositionsFromNodes(nodesRef.current),
    [],
  );

  const persistNodePositions = useCallback(
    async (positions: U2000ReactFlowNodePositions): Promise<boolean> => {
      if (!onSavePositions) {
        return false;
      }
      const result = await onSavePositions(positions);
      if (result === false) {
        return false;
      }
      positionsBaselineRef.current = { ...positions };
      return true;
    },
    [onSavePositions],
  );

  const isPositionsDirty = useCallback(() => {
    if (!closeGuardEnabled) {
      return false;
    }
    const current = computeCurrentPositions();
    if (extendBaselineBeforeCloseCheckRef.current) {
      extendBaselineBeforeCloseCheckRef.current = false;
      positionsBaselineRef.current = u2000ExtendPositionsBaselineForNewNodes(
        positionsBaselineRef.current,
        current,
      );
    }
    return u2000IsPositionsMapDirty(positionsBaselineRef.current, current);
  }, [closeGuardEnabled, computeCurrentPositions]);

  const handleRequestClose = useCallback(
    () => {
      if (!open) {
        return;
      }
      if (suspendCloseGuard || !closeGuardEnabled) {
        onClose();
        return;
      }
      if (!isPositionsDirty()) {
        onClose();
        return;
      }
      setUnsavedCloseConfirmOpen(true);
    },
    [open, suspendCloseGuard, closeGuardEnabled, isPositionsDirty, onClose],
  );

  const handleUnsavedCloseDiscard = useCallback(() => {
    setUnsavedCloseConfirmOpen(false);
    onClose();
  }, [onClose]);

  const handleUnsavedCloseCancel = useCallback(() => {
    setUnsavedCloseConfirmOpen(false);
  }, []);

  const handleUnsavedCloseSaveAndExit = useCallback(async () => {
    setIsSavingPositionsOnClose(true);
    try {
      const positions = computeCurrentPositions();
      const ok = await persistNodePositions(positions);
      if (ok) {
        setUnsavedCloseConfirmOpen(false);
        onClose();
      }
    } finally {
      setIsSavingPositionsOnClose(false);
    }
  }, [computeCurrentPositions, persistNodePositions, onClose]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  React.useEffect(() => {
    if (liveGraphNodesRef) {
      liveGraphNodesRef.current = nodes;
    }
  }, [nodes, liveGraphNodesRef]);

  React.useEffect(() => {
    if (positionsBaselineCommitKey == null) {
      return;
    }
    if (positionsBaselineCommitKey === baselineCommitKeySeenRef.current) {
      return;
    }
    baselineCommitKeySeenRef.current = positionsBaselineCommitKey;
    pendingBaselineCommitKeyRef.current = positionsBaselineCommitKey;
    extendBaselineBeforeCloseCheckRef.current = true;
  }, [positionsBaselineCommitKey]);

  /** BL-0711 — resync baseline complète une fois le merge canvas terminé (create lot graphe). */
  React.useLayoutEffect(() => {
    if (!open || !closeGuardEnabled || pendingBaselineCommitKeyRef.current == null) {
      return;
    }
    if (initialNodes.length === 0) {
      return;
    }
    const liveIds = new Set(nodes.map((n) => n.id));
    const snapshotReady = initialNodes.every((n) => liveIds.has(n.id));
    if (!snapshotReady) {
      return;
    }
    positionsBaselineRef.current = u2000CollectNodePositionsFromNodes(nodes);
    pendingBaselineCommitKeyRef.current = undefined;
    extendBaselineBeforeCloseCheckRef.current = false;
  }, [open, closeGuardEnabled, nodes, initialNodes]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds) => u2000MapEdgesSelectionVisual(applyEdgeChanges(changes, eds)));
  }, []);

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      event.stopPropagation();
      setEdges((eds) =>
        u2000MapEdgesSelectionVisual(eds.map((e) => ({ ...e, selected: e.id === edge.id }))),
      );
    },
    [],
  );

  const defaultPaneMenuItems = useMemo<U2000ReactFlowPaneContextMenuItem[]>(() => {
    const items: U2000ReactFlowPaneContextMenuItem[] = [];
    if (onSavePositions) {
      items.push({
        key: "savePositions",
        label: savePositionsLabel,
        icon: FaSave,
        itemVariant: "action",
        onClick: () => {
          void persistNodePositions(u2000CollectNodePositionsFromNodes(nodes)).then(() => {
            setPaneContextMenu(null);
          });
        },
      });
    }
    if (onResetLayout) {
      items.push({
        key: "resetLayout",
        label: resetLayoutLabel,
        icon: FaUndo,
        itemVariant: "action",
        onClick: () => {
          onResetLayout();
          setPaneContextMenu(null);
        },
      });
    }
    return items;
  }, [onSavePositions, onResetLayout, savePositionsLabel, resetLayoutLabel, nodes, persistNodePositions]);

  const effectivePaneMenuItems = useMemo<U2000ReactFlowPaneContextMenuItem[]>(() => {
    const custom = paneContextMenuItemsProp ?? [];
    if (custom.length > 0 && defaultPaneMenuItems.length > 0) {
      return [...custom, ...defaultPaneMenuItems];
    }
    if (custom.length > 0) return custom;
    return defaultPaneMenuItems;
  }, [paneContextMenuItemsProp, defaultPaneMenuItems]);

  const hasPaneContextMenu = effectivePaneMenuItems.length > 0;

  const effectivePaneGroupedMenuItems = useMemo((): U2000ReactFlowContextMenuItem[] => {
    const addSection: U2000ReactFlowContextMenuItem[] = [];
    const layoutSection: U2000ReactFlowContextMenuItem[] = [];
    effectivePaneMenuItems.forEach((paneItem) => {
      const row: U2000ReactFlowContextMenuItem = {
        key: paneItem.key,
        label: paneItem.label,
        icon: paneItem.icon,
        itemVariant: paneItem.itemVariant ?? "action",
        disabled: paneItem.disabled,
        onClick: () => paneItem.onClick(),
      };
      if (U2000_PANE_MENU_ADD_KEYS.has(paneItem.key)) {
        addSection.push(row);
      } else {
        layoutSection.push(row);
      }
    });
    const grouped: U2000ReactFlowContextMenuItem[] = [];
    u2000AppendReactFlowContextMenuSection(grouped, "pane-section-add", paneMenuSectionAddLabel, addSection);
    u2000AppendReactFlowContextMenuSection(grouped, "pane-section-layout", paneMenuSectionLayoutLabel, layoutSection);
    return grouped;
  }, [effectivePaneMenuItems, paneMenuSectionAddLabel, paneMenuSectionLayoutLabel]);

  // Réinitialiser nodes/edges et fermer les menus contextuels : le BasicDialog retourne null
  // quand open=false mais ce composant reste monté — sans quoi un menu ouvert reste en état et
  // réapparaît à la prochaine ouverture (sans nouveau clic droit).
  React.useEffect(() => {
    setContextMenu(null);
    setEdgeContextMenu(null);
    setPaneContextMenu(null);
    if (!open) {
      graphOpenSessionRef.current = false;
      lastAppliedGraphLayoutKeyRef.current = undefined;
      positionsBaselineRef.current = {};
      baselineCommitKeySeenRef.current = undefined;
      pendingBaselineCommitKeyRef.current = undefined;
      extendBaselineBeforeCloseCheckRef.current = false;
      setUnsavedCloseConfirmOpen(false);
      return;
    }
    const refreshWhileOpen = graphOpenSessionRef.current;
    graphOpenSessionRef.current = true;

    const forceRelayoutFromKey =
      initialGraphLayoutKey != null && initialGraphLayoutKey !== lastAppliedGraphLayoutKeyRef.current;

    if (refreshWhileOpen && !forceRelayoutFromKey) {
      setNodes((current) => {
        const merged = u2000MergeGraphSnapshotPreservingLayout(current, initialNodes);
        if (closeGuardEnabled && pendingBaselineCommitKeyRef.current != null) {
          positionsBaselineRef.current = u2000CollectNodePositionsFromNodes(merged);
          pendingBaselineCommitKeyRef.current = undefined;
        }
        return merged;
      });
      setEdges(u2000MapEdgesSelectionVisual(initialEdges));
      return;
    }

    if (forceRelayoutFromKey) {
      lastAppliedGraphLayoutKeyRef.current = initialGraphLayoutKey;
    }

    const next = resolveInitialGraphLayout(initialNodes, initialEdges);
    setNodes(next.nodes);
    setEdges(u2000MapEdgesSelectionVisual(next.edges));
    if (closeGuardEnabled) {
      positionsBaselineRef.current = u2000CollectNodePositionsFromNodes(next.nodes);
    }
  }, [open, initialNodes, initialEdges, initialGraphLayoutKey, closeGuardEnabled]);

  // Retourne tous les ancêtres d’un nœud (parents, grands-parents, etc.) en remontant les edges target → source.
  const getAncestorIds = useCallback((nodeId: string, edgeList: Edge[]) => {
    const ids: string[] = [];
    const visited = new Set<string>();
    let queue = edgeList.filter((e) => e.target === nodeId).map((e) => e.source);
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      ids.push(id);
      queue = queue.concat(edgeList.filter((e) => e.target === id).map((e) => e.source));
    }
    return ids;
  }, []);

  // Retourne tous les descendants d’un nœud (enfants, petits-enfants, etc.) en suivant les edges source → target.
  const getDescendantIds = useCallback((sourceId: string, edgeList: Edge[]) => {
    const ids: string[] = [];
    const visited = new Set<string>();
    let queue = edgeList.filter((e) => e.source === sourceId).map((e) => e.target);
    while (queue.length > 0) {
      const id = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);
      ids.push(id);
      queue = queue.concat(edgeList.filter((e) => e.source === id).map((e) => e.target));
    }
    return ids;
  }, []);

  // Clic sur la case à cocher du nœud : met à jour ce nœud et tous ses descendants, puis notifie le parent en un seul appel bulk.
  // Les ids envoyés au backend sont les child lot chain ids (nodePayload.id), pas les node ids (ex. lotDetail.id).
  const handleNodeVisibleChange = useCallback(
    (nodeId: string, visible: boolean) => {
      const descendantIds = getDescendantIds(nodeId, edges);
      const allNodeIds = [nodeId, ...descendantIds];
      setNodes((prev) => {
        const next = prev.map((n) =>
          allNodeIds.includes(n.id) ? { ...n, data: { ...n.data, visible } } : n
        );
        const childLotChainIds = allNodeIds
          .map((id) => (prev.find((n) => n.id === id)?.data?.nodePayload as { id?: string } | undefined)?.id)
          .filter((id): id is string => Boolean(id));
        if (masterLotChainId && onNodeVisibleChange && childLotChainIds.length > 0) {
          onNodeVisibleChange({ masterLotChainId, childLotChainIds, visible });
        }
        return next;
      });
    },
    [edges, getDescendantIds, masterLotChainId, onNodeVisibleChange]
  );

  const contextValue = useMemo<U2000ReactFlowDialogContextValue>(
    () => ({
      onNodeVisibleChange: handleNodeVisibleChange,
      showCheckbox,
      checkboxDisabled,
      isCheckboxDisabledByAncestor: (nodeId: string) => {
        const ancestorIds = getAncestorIds(nodeId, edges);
        return nodes.some((n) => ancestorIds.includes(n.id) && n.data?.visible === true);
      },
      masterLotChainId,
    }),
    [handleNodeVisibleChange, showCheckbox, checkboxDisabled, getAncestorIds, edges, nodes, masterLotChainId]
  );

  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      setPaneContextMenu(null);
      setEdgeContextMenu(null);
      setContextMenu({ node, x: event.clientX, y: event.clientY });
    },
    []
  );

  const onEdgeContextMenu = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (!getEdgeContextMenuItems) return;
      event.preventDefault();
      setPaneContextMenu(null);
      setContextMenu(null);
      setEdgeContextMenu({ edge, x: event.clientX, y: event.clientY });
      setEdges((eds) =>
        u2000MapEdgesSelectionVisual(eds.map((e) => ({ ...e, selected: e.id === edge.id }))),
      );
    },
    [getEdgeContextMenuItems],
  );

  const onPaneClick = useCallback(() => {
    setContextMenu(null);
    setEdgeContextMenu(null);
    setPaneContextMenu(null);
    setEdges((eds) => u2000MapEdgesSelectionVisual(eds.map((e) => ({ ...e, selected: false }))));
  }, []);

  const onPaneContextMenu = useCallback((event: React.MouseEvent | MouseEvent) => {
    event.preventDefault();
    if (!hasPaneContextMenu) return;
    setContextMenu(null);
    setEdgeContextMenu(null);
    setPaneContextMenu({ x: event.clientX, y: event.clientY });
  }, [hasPaneContextMenu]);

  const effectiveContextMenuItems = useMemo(
    () => (getContextMenuItems && contextMenu ? getContextMenuItems(contextMenu.node) : contextMenuItems),
    [getContextMenuItems, contextMenu, contextMenuItems]
  );
  const effectiveEdgeContextMenuItems = useMemo(
    () => (getEdgeContextMenuItems && edgeContextMenu ? getEdgeContextMenuItems(edgeContextMenu.edge) : []),
    [getEdgeContextMenuItems, edgeContextMenu],
  );
  const hasContextMenu = contextMenuItems.length > 0 || !!getContextMenuItems;
  const hasEdgeContextMenu = !!getEdgeContextMenuItems;
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const paneContextMenuRef = useRef<HTMLDivElement>(null);

  // Position du menu dans le viewport (évite débordement)
  const contextMenuPosition = useMemo(() => {
    if (!contextMenu) return null;
    const menuWidth = 200;
    const menuHeight = u2000EstimateGroupedContextMenuHeight(effectiveContextMenuItems);
    const margin = U2000_CONTEXT_MENU_VIEWPORT_MARGIN;
    let left = contextMenu.x;
    let top = u2000ResolveContextMenuVerticalTop(contextMenu.y, menuHeight);
    if (left + menuWidth > window.innerWidth - margin) left = window.innerWidth - menuWidth - margin;
    if (left < margin) left = margin;
    return { left, top };
  }, [contextMenu, effectiveContextMenuItems]);

  const paneContextMenuPosition = useMemo(() => {
    if (!paneContextMenu) return null;
    const menuWidth = 220;
    const menuHeight = u2000EstimateGroupedContextMenuHeight(effectivePaneGroupedMenuItems);
    const margin = U2000_CONTEXT_MENU_VIEWPORT_MARGIN;
    let left = paneContextMenu.x;
    let top = u2000ResolveContextMenuVerticalTop(paneContextMenu.y, menuHeight);
    if (left + menuWidth > window.innerWidth - margin) left = window.innerWidth - menuWidth - margin;
    if (left < margin) left = margin;
    return { left, top };
  }, [paneContextMenu, effectivePaneGroupedMenuItems]);

  const edgeContextMenuPosition = useMemo(() => {
    if (!edgeContextMenu) return null;
    const menuWidth = 220;
    const menuHeight = u2000EstimateGroupedContextMenuHeight(
      effectiveEdgeContextMenuItems as U2000ReactFlowContextMenuItem[],
    );
    const margin = U2000_CONTEXT_MENU_VIEWPORT_MARGIN;
    let left = edgeContextMenu.x;
    let top = u2000ResolveContextMenuVerticalTop(edgeContextMenu.y, menuHeight);
    if (left + menuWidth > window.innerWidth - margin) left = window.innerWidth - menuWidth - margin;
    if (left < margin) left = margin;
    return { left, top };
  }, [edgeContextMenu, effectiveEdgeContextMenuItems]);

  const edgeContextMenuRef = useRef<HTMLDivElement>(null);

  // À l'ouverture : focus sur le premier item non désactivé
  useEffect(() => {
    if (!contextMenu || !contextMenuPosition || !contextMenuRef.current) return;
    const firstItem = contextMenuRef.current.querySelector<HTMLButtonElement>(
      '[role="menuitem"]:not(:disabled)'
    );
    requestAnimationFrame(() => firstItem?.focus());
  }, [contextMenu, contextMenuPosition]);

  // Clavier : Escape ferme ; flèches naviguent ; Enter/Espace active l'item
  useEffect(() => {
    if (!contextMenu || !contextMenuPosition || !contextMenuRef.current) return;
    const menu = contextMenuRef.current;
    const items = menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)');
    const count = items.length;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.target || !menu.contains(e.target as HTMLElement)) return;
      if ((e.target as HTMLElement).getAttribute("role") !== "menuitem") return;

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setContextMenu(null);
          return;
        case "ArrowDown":
          e.preventDefault();
          if (count > 0) {
            const idx = Array.from(items).indexOf(e.target as HTMLButtonElement);
            const next = idx < count - 1 ? items[idx + 1] : items[0];
            next?.focus();
          }
          return;
        case "ArrowUp":
          e.preventDefault();
          if (count > 0) {
            const idx = Array.from(items).indexOf(e.target as HTMLButtonElement);
            const prev = idx > 0 ? items[idx - 1] : items[count - 1];
            prev?.focus();
          }
          return;
        case "Enter":
        case " ":
          e.preventDefault();
          (e.target as HTMLButtonElement).click();
          return;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [contextMenu, contextMenuPosition, effectiveContextMenuItems]);

  return (
    <U2000ReactFlowDialogContext.Provider value={contextValue}>
      {closeGuardEnabled && unsavedPositionsCloseLabels ? (
        <U2000UnsavedNodePositionsDialog
          isOpen={unsavedCloseConfirmOpen}
          labels={unsavedPositionsCloseLabels}
          showSaveAction={!checkboxDisabled}
          isSaving={isSavingPositionsOnClose}
          onSaveAndExit={() => void handleUnsavedCloseSaveAndExit()}
          onDiscard={handleUnsavedCloseDiscard}
          onCancel={handleUnsavedCloseCancel}
        />
      ) : null}
      <U2000BasicDialog isOpen={open} onClose={handleRequestClose} title={title} width="w-[75vw]" height="h-[75vh]">
        <div className="u2000-react-flow-dialog relative h-[calc(75vh-4.5rem)] w-full overflow-hidden rounded-lg border border-gray-200">
          <ReactFlowProvider>
            <U2000ReactFlowCanvas
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onNodeContextMenu={hasContextMenu ? onNodeContextMenu : undefined}
              onEdgeContextMenu={hasEdgeContextMenu ? onEdgeContextMenu : undefined}
              onPaneClick={onPaneClick}
              onPaneContextMenu={onPaneContextMenu}
              onPaneContextMenuOpen={onPaneContextMenuOpen}
              onConnect={onConnect}
              isValidConnection={isValidConnection}
              onEdgesDelete={onEdgesDelete}
              onEdgeClick={onEdgeClick}
              nodesConnectable={nodesConnectable}
              edgesDeletable={edgesDeletable}
            />
          </ReactFlowProvider>
          {contextMenu && hasContextMenu && contextMenuPosition && effectiveContextMenuItems.length > 0 && (
            <U2000ReactFlowGroupedContextMenu
              items={effectiveContextMenuItems}
              menuAriaLabel="Actions du nœud"
              position={contextMenuPosition}
              menuRef={contextMenuRef}
              resolveDisabled={(item) =>
                typeof item.disabled === "function" ? item.disabled(contextMenu.node) : item.disabled ?? false
              }
              onItemActivate={(item) => {
                item.onClick?.(contextMenu.node);
                setContextMenu(null);
              }}
            />
          )}
          {edgeContextMenu &&
            hasEdgeContextMenu &&
            edgeContextMenuPosition &&
            effectiveEdgeContextMenuItems.length > 0 && (
              <U2000ReactFlowGroupedContextMenu
                items={effectiveEdgeContextMenuItems as U2000ReactFlowContextMenuItem[]}
                menuAriaLabel="Actions de la liaison"
                position={edgeContextMenuPosition}
                menuRef={edgeContextMenuRef}
                resolveDisabled={(item) =>
                  typeof item.disabled === "function"
                    ? (item.disabled as (edge: Edge) => boolean)(edgeContextMenu.edge)
                    : item.disabled ?? false
                }
                onItemActivate={(item) => {
                  (item.onClick as ((edge: Edge) => void) | undefined)?.(edgeContextMenu.edge);
                  setEdgeContextMenu(null);
                }}
              />
            )}
          {paneContextMenu && hasPaneContextMenu && paneContextMenuPosition && effectivePaneGroupedMenuItems.length > 0 && (
            <U2000ReactFlowGroupedContextMenu
              items={effectivePaneGroupedMenuItems}
              menuAriaLabel="Actions du graphe"
              position={paneContextMenuPosition}
              menuRef={paneContextMenuRef}
              minWidthClass="min-w-[240px]"
              resolveDisabled={(item) =>
                typeof item.disabled === "function" ? item.disabled({} as Node) : item.disabled ?? false
              }
              onItemActivate={(item) => {
                item.onClick?.({} as Node);
                setPaneContextMenu(null);
              }}
            />
          )}
        </div>
      </U2000BasicDialog>
    </U2000ReactFlowDialogContext.Provider>
  );
};

export default U2000ReactFlowDialog;
